import React, { useState, useRef, useEffect } from "react";
import ProgressBar from "../ProgressBar";
import LessonUI from "../ScienceLessonUI";
import { generateAdvancedScienceQuiz } from "../AdvanceScienceQuizPool";
import defaultProfile from "../../UserProfile/EmptyProfile.jpg";
import { ScienceStoryModeQuiz } from "../ScienceStoryModeStructure";
import { awardToken } from "../../awardtoken.js";

// All lesson pages
export const Grade11PhysicsLessonPages = [
    {
      title: "Introduction to Grade 11 Physics",
      content: (
        <>
          <p>
            Welcome to Grade 11 Physics! This year, you’ll explore the rules of motion, forces, energy, waves, and electricity—foundations for understanding the universe and building the technology of tomorrow.
          </p>
          <ul>
            <li><b>Kinematics & Dynamics:</b> How and why do things move?</li>
            <li><b>Waves & Sound:</b> How does energy travel without moving matter?</li>
            <li><b>Electricity & Magnetism:</b> How do invisible forces shape the modern world?</li>
          </ul>
          <p>
            <b>Big Question:</b> How does physics help us tackle global challenges, design new technologies, and imagine the future?
          </p>
          <p>
            <i>Look Ahead:</i> Physics is key for careers in engineering, robotics, medicine, climate science, and more.
          </p>
        </>
      ),
    },
    {
      title: "Chapter 1: Kinematics & Dynamics",
      topic: "Kinematics & Dynamics",
      content: (
        <>
          <h4>The Science of Motion</h4>
          <ul>
            <li>Describing and analyzing motion: distance, displacement, speed, velocity, acceleration</li>
            <li><b>Newton’s Laws of Motion:</b> Explaining everything from falling apples to rocket launches</li>
            <li>Free-body diagrams: Visualizing forces, friction, gravity, and normal force in real life</li>
            <li>Using kinematic equations to predict and optimize motion in sports, vehicles, or robots</li>
            <li>Case studies: Why do airbags work? How do engineers design safe roller coasters?</li>
          </ul>
          <p>
            <b>Try this:</b> Use your phone or a stopwatch to record the time for a ball or toy car to travel a set distance. Calculate average speed and acceleration.
          </p>
          <p>
            <i>Futurist Angle:</i> How do autonomous vehicles or drones use physics to safely navigate changing environments?
          </p>
        </>
      ),
    },
    {
      title: "Chapter 2: Waves & Sound",
      topic: "Waves & Sound",
      content: (
        <>
          <h4>Energy on the Move</h4>
          <ul>
            <li>What is a wave? Comparing transverse and longitudinal waves</li>
            <li>Properties of waves: frequency, wavelength, amplitude, speed—and how they affect sound and light</li>
            <li>Resonance, interference, reflection, refraction, diffraction: The physics behind musical instruments, echoes, and rainbows</li>
            <li>Real-world sound: The Doppler effect (ambulances!), noise pollution, and soundproofing</li>
            <li>Applications: Ultrasound in medicine, seismology, sonar for underwater exploration</li>
          </ul>
          <p>
            <b>Experiment:</b> Create standing waves using a skipping rope or string. What happens when you change the tension or frequency?
          </p>
          <p>
            <i>Modern Physics:</i> How do scientists use sound waves to map the ocean floor or detect earthquakes? What’s next for acoustic technology?
          </p>
        </>
      ),
    },
    {
      title: "Chapter 3: Electricity & Magnetism",
      topic: "Electricity & Magnetism",
      content: (
        <>
          <h4>Invisible Forces, Visible Impact</h4>
          <ul>
            <li>Electric charge, current, voltage, resistance: Making sense of everyday electronics</li>
            <li><b>Ohm’s Law:</b> How do circuits work in practice? Building and analyzing real circuits</li>
            <li>Magnetism: From simple magnets to Earth’s magnetic field and magnetic levitation</li>
            <li>Electromagnetic induction: How do generators, transformers, and electric cars work?</li>
            <li>Cutting-edge tech: Wireless charging, maglev trains, MRI machines, and the future of energy</li>
          </ul>
          <p>
            <b>DIY:</b> Build a basic circuit using batteries, wires, and a bulb (or simulate one online). What happens when you add or remove components?
          </p>
          <p>
            <i>Frontiers:</i> How are engineers using electromagnetism for renewable energy, medical diagnostics, and quantum computing?
          </p>
        </>
      ),
    },
    {
      title: "Final Review & Looking Ahead",
      isFinalReview: true,
      content: (
        <>
          <p>
            Physics underpins the world’s most important technologies—from cell phones to satellites to renewable energy. Mastering physics gives you tools to analyze, invent, and make a difference.
          </p>
          <ul>
            <li>Review key equations and concepts from each chapter</li>
            <li>Explore recent breakthroughs: gravitational waves, electric vehicles, smart sensors, and more</li>
            <li>Reflect: What was the most surprising thing you learned this year? How can physics help you in your future career?</li>
          </ul>
          <p>
            <b>Next Steps:</b> Keep questioning, exploring, and looking for the physics in everything around you!
          </p>
        </>
      ),
    },
  ];
  

export default function Grade11PhysicsLesson({ onAwardTokens }) {
    const [current, setCurrent] = useState(0);
    const [showQuiz, setShowQuiz] = useState(null); // null | "chapter" | "final"
    const [quizResult, setQuizResult] = useState(null);
    const [user, setUser] = useState(null);
    const hasAwardedToken = useRef(false);
  
    // Track which chapters have been passed
    const [chaptersPassed, setChaptersPassed] = useState(() =>
      JSON.parse(localStorage.getItem("phys11ChaptersPassed") || "{}")
    );
  
    // Load user avatar/info for quiz
    useEffect(() => {
      const logged = JSON.parse(localStorage.getItem("loggedInUser"));
      if (logged) setUser(logged);
    }, []);
  
    const currentTopic = Grade11PhysicsLessonPages[current]?.topic;
  
    // Determine quiz pool for current chapter OR final
    let quizQuestions = [];
    if (showQuiz === "chapter" && currentTopic) {
      quizQuestions = generateAdvancedScienceQuiz({
        subject: "Physics",
        grade: 11,
        topic: currentTopic,
        count: 5,
      });
    } else if (showQuiz === "final") {
      const topics = [
        "Kinematics & Dynamics",
        "Work, Energy & Power",
        "Waves & Sound",
        "Electricity & Magnetism"
      ];
      let pool = [];
      topics.forEach(topic => {
        pool = pool.concat(generateAdvancedScienceQuiz({
          subject: "Physics",
          grade: 11,
          topic,
          count: 10,
        }));
      });
      quizQuestions = pool.sort(() => Math.random() - 0.5).slice(0, 15);
    }
  
    // Award token if ≥80% and not yet awarded this attempt
    useEffect(() => {
      if (quizResult && !hasAwardedToken.current) {
        const percent = (quizResult.score / quizResult.total) * 100;
        if (percent >= 80) {
          if (typeof onAwardTokens === "function") {
            onAwardTokens(1);
          } else {
            awardToken();
          }
          hasAwardedToken.current = true;
        }
      }
    }, [quizResult, onAwardTokens]);
  
    // Gated navigation handler
    const handleNextPage = () => {
        const nextIndex = current + 1;
        const nextPage = Grade11PhysicsLessonPages[nextIndex];
      
        // Only gate if the current page is a chapter (i.e., has a topic)
        if (
          nextPage &&
          nextPage.topic !== currentTopic &&
          currentTopic &&          // <--- fix: only gate if topic exists
          !chaptersPassed[currentTopic]
        ) {
          setShowQuiz("chapter");
          setQuizResult(null);
          return;
        }
        setCurrent(nextIndex);
      };
      
  
    // Quiz UI handler (update chaptersPassed if passed)
    if (showQuiz && quizQuestions.length > 0) {
      return (
        <ScienceStoryModeQuiz
  questions={quizQuestions}
  onFinish={(score, total) => {
    setShowQuiz(null);
    setQuizResult({ score, total });
    hasAwardedToken.current = false;
    const percent = total > 0 ? Math.round((score / total) * 100) : 0;

    // --- Save quiz result for Student Dashboard ---
    if (showQuiz === "chapter" && currentTopic) {
      const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
      quizResults[`physics_g11_${currentTopic}`] = {
        score,
        total,
        percent,
        lastCompleted: Date.now(),
      };
      localStorage.setItem("quizResults", JSON.stringify(quizResults));
    }
    if (showQuiz === "final") {
      const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
      quizResults["physics_g11_final"] = {
        score,
        total,
        percent,
        lastCompleted: Date.now(),
      };
      localStorage.setItem("quizResults", JSON.stringify(quizResults));
    }
    // ---------------------------------------------

    if (percent >= 80 && currentTopic && showQuiz === "chapter") {
      setChaptersPassed(prev => {
        const updated = { ...prev, [currentTopic]: true };
        localStorage.setItem("phys11ChaptersPassed", JSON.stringify(updated));
        return updated;
      });
    }
  }}
  studentAvatar={user?.avatar ?? defaultProfile}
/>
      );
    }
  
    // Quiz result/congrats screen
    if (quizResult) {
      const percent = Math.round((quizResult.score / quizResult.total) * 100);
      const passed = percent >= 80;
      return (
        <div className="quiz-result-container" style={{ textAlign: "center", marginTop: 48 }}>
          <h2 style={{ color: "#d4145a" }}>Quiz Complete!</h2>
          <p style={{ fontSize: 22, margin: "18px 0" }}>
            Your Score: {quizResult.score} / {quizResult.total} ({percent}%)
          </p>
          {passed ? (
            <div style={{ color: "#228B22", fontSize: 24, margin: "16px 0" }}>
              🎉 Congratulations! You earned a Science token!
            </div>
          ) : (
            <div style={{ color: "#e67e22", fontSize: 20, margin: "16px 0" }}>
              Try again to earn a Science token (80% required).
            </div>
          )}
          {passed ? (
            <button
              className="button1"
              style={{ marginTop: 28 }}
              onClick={() => {
                setQuizResult(null);
                setCurrent(c => c + 1);
              }}
            >
              Next Chapter
            </button>
          ) : (
            <button
              className="button1"
              style={{ marginTop: 28 }}
              onClick={() => setQuizResult(null)}
            >
              Return to Lesson
            </button>
          )}
        </div>
      );
    }
  
    // Main lesson rendering
    return (
      <div className="lesson-page">
        <ProgressBar progress={Math.round((current / (Grade11PhysicsLessonPages.length - 1)) * 100)} />
        <LessonUI
          pages={[Grade11PhysicsLessonPages[current]]}
          currentPageIndex={0}
          onPrevPage={current > 0 ? () => setCurrent(c => c - 1) : undefined}
          onNextPage={current < Grade11PhysicsLessonPages.length - 1 ? handleNextPage : undefined}
          backTo="/science"
        />
        {/* Chapter quiz (for slides with a topic) */}
        {currentTopic && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button className="button1" onClick={() => {
              setShowQuiz("chapter");
              setQuizResult(null);
              hasAwardedToken.current = false;
            }}>
              Start Quiz for This Chapter
            </button>
          </div>
        )}
        {/* Final Quiz on the last slide */}
        {Grade11PhysicsLessonPages[current]?.isFinalReview && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button className="button1" onClick={() => {
              setShowQuiz("final");
              setQuizResult(null);
              hasAwardedToken.current = false;
            }}>
              Take Final Quiz for Grade 11 Physics
            </button>
          </div>
        )}
      </div>
    );
  }
  